// ============================================================================
// CONFIRM MODAL COMPONENT
// Reusable confirmation dialog component
// ============================================================================

import React from 'react';
import { X, AlertTriangle } from 'lucide-react';

interface ConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info';
  isProcessing?: boolean;
}

export const ConfirmModal: React.FC<ConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'warning',
  isProcessing = false
}) => {
  if (!isOpen) return null;

  const variantColors = {
    danger: {
      icon: '#fa5252',
      button: '#fa5252',
      buttonHover: '#e03131'
    },
    warning: {
      icon: '#fd7e14',
      button: '#fd7e14',
      buttonHover: '#f76707'
    },
    info: {
      icon: '#228be6',
      button: '#228be6',
      buttonHover: '#1c7ed6'
    }
  };

  const colors = variantColors[variant];

  const handleConfirm = () => {
    if (!isProcessing) {
      onConfirm();
    }
  };

  const handleCancel = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
      onClick={handleCancel}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '8px',
          maxWidth: '500px',
          width: '100%',
          boxShadow: '0 10px 40px rgba(0, 0, 0, 0.2)',
          overflow: 'hidden'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid #e9ecef',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertTriangle size={24} color={colors.icon} />
            <h2 style={{ fontSize: '18px', fontWeight: '600', margin: 0 }}>
              {title}
            </h2>
          </div>
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            style={{
              background: 'none',
              border: 'none',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              opacity: isProcessing ? 0.5 : 1
            }}
          >
            <X size={20} color="#868e96" />
          </button>
        </div>

        {/* Content */}
        <div style={{ padding: '24px' }}>
          <p
            style={{
              margin: 0,
              color: '#495057',
              fontSize: '15px',
              lineHeight: '1.6'
            }}
          >
            {message}
          </p>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '16px 24px',
            borderTop: '1px solid #e9ecef',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '12px',
            background: '#f8f9fa'
          }}
        >
          <button
            onClick={handleCancel}
            disabled={isProcessing}
            style={{
              padding: '8px 16px',
              background: 'white',
              color: '#495057',
              border: '1px solid #ced4da',
              borderRadius: '6px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              opacity: isProcessing ? 0.5 : 1
            }}
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={isProcessing}
            style={{
              padding: '8px 16px',
              background: isProcessing ? '#adb5bd' : colors.button,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: isProcessing ? 'not-allowed' : 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => {
              if (!isProcessing) {
                e.currentTarget.style.background = colors.buttonHover;
              }
            }}
            onMouseOut={(e) => {
              if (!isProcessing) {
                e.currentTarget.style.background = colors.button;
              }
            }}
          >
            {isProcessing ? 'Processing...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};
